import type { IAggFuncParams } from 'ag-grid-community';
import { WarningSeverity } from '../../../types/WarningSeverity';
import type { WarningColumnValue } from '../columns/warning.types';

/**
 * Aggregation function for warning columns that reduces a list of warning severities
 * into the one highest severity.
 *
 * Supports tree blotters where group rows have their own data by checking if the
 * rowNode has data and considering that value alongside child values.
 */
export function warningAggFunc({
  values,
  rowNode,
  api,
  column,
}: IAggFuncParams<unknown, WarningColumnValue | undefined>): WarningColumnValue | undefined {
  // myValue exists to support tree blotters where group rows have their own data. Recall that aggFuncs are only called when rowNode is a group node,
  // so at this point we are either a regular group row or a tree group row. We distinguish between the two by checking if this groupRow has any data of
  // its own, in which case this aggFunc needs to take it into consideration.
  const isTreeGroupRow = rowNode.data != null;
  const myValue: WarningColumnValue | undefined = isTreeGroupRow
    ? (api.getCellValue({ colKey: column.getColId(), rowNode }) ?? undefined)
    : undefined;

  const highestChildValue = values.reduce(
    (aggregate: WarningColumnValue | undefined, value) => {
      if (value == null) {
        return aggregate;
      }

      // If nothing has been instantiated yet, set the memory as this first value
      if (aggregate?.severity == null) {
        return value;
      }

      if (aggregate.severity < value.severity) {
        return value;
      }

      return aggregate;
    },
    undefined as WarningColumnValue | undefined
  );

  // Based on the severity, we return the _complete_ values.
  return (myValue?.severity ?? WarningSeverity.NONE) > (highestChildValue?.severity ?? WarningSeverity.NONE)
    ? myValue
    : highestChildValue;
}
